import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { theme } from '@notino/react-styleguide';

import PriceLabel from '@components/PriceLabel';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';

import { usePriceLabelState } from '../../SelectedVariant/usePriceLabelState';
import { BottomDiscountPart } from '../BottomDiscountPart';
import { useVoucherDiscountContext } from '../context';
import { messages } from '../messages';
import {
  Wrapper,
  CurrencyWrapper,
  Slash,
  PriceBelowMinConditionWrapper,
} from '../styled';
import { VoucherDiscountInfo } from '../VoucherDiscountInfo';

import { ConditionalInstructionsText } from './ConditionalInstructionsText';

export interface IConditionalVoucherDiscount {
  code: string;
  conditionType: 'Price' | 'Piece';
  discountType: 'Absolute' | 'Percentage';
  productsOnSaleIncluded: boolean;
  discountConditions: Array<{
    percentageDiscount: number;
    discountedPrice: number;
    discountAmount: number;
    productMeetsCondition: boolean;
    conditionMin: number;
    percentageFromMinimal: number | undefined;
  }>;
  targets: Array<{
    name: string;
    type: 'Brand' | 'Category';
    url: string;
  }>;
  tags?: string[];
  validUntil: string;
}

export interface IVoucherDiscountProps {
  voucherDiscount: IConditionalVoucherDiscount;
}

export const ConditionalVoucherDiscount = ({
  voucherDiscount,
}: IVoucherDiscountProps) => {
  const {
    isDualPrice,
    getLeftSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
    getRightSidePrice,
  } = useIsDualPrice();
  const { currency } = useVoucherDiscountContext();
  const priceState = usePriceLabelState();

  const showBottomSection = voucherDiscount.discountType === 'Percentage';

  const {
    code,
    productsOnSaleIncluded,
    discountType,
    discountConditions: [
      {
        discountedPrice,
        discountAmount,
        percentageDiscount,
        percentageFromMinimal,
      },
    ],
  } = voucherDiscount;

  const getPriceComponent = (price: number) => (
    <>
      {isDualPrice && (
        <Wrapper>
          {getLeftSideFormattedPrice(price)}{' '}
          <CurrencyWrapper>{leftSideCurrency}</CurrencyWrapper>
          <Slash>/</Slash>
        </Wrapper>
      )}
      <PriceLabel
        price={{
          value: isDualPrice ? getRightSidePrice(price) : price,
          currency: currency.code,
        }}
        wrapperComponent={
          priceState === 'priceBelowMinCondition'
            ? PriceBelowMinConditionWrapper
            : Wrapper
        }
        currencyComponent={CurrencyWrapper}
        currencySymbolOverride={isDualPrice ? rightSideCurrency : undefined}
      />
    </>
  );

  const getInfoTooltipText = () => {
    if (showBottomSection) {
      return;
    }

    return productsOnSaleIncluded ? (
      <FormattedMessage {...messages.conditionalTooltipSaleIncludedText} />
    ) : (
      <FormattedMessage {...messages.conditionalTooltipText} />
    );
  };

  const discountTypeComponentMap: Record<typeof discountType, JSX.Element> = {
    Percentage: getPriceComponent(discountedPrice),
    Absolute: (
      <FormattedMessage
        {...messages.discountAmountAbsolute}
        values={{ amountPrice: getPriceComponent(discountAmount) }}
      />
    ),
  };

  return (
    <VoucherDiscountInfo
      discountCode={code}
      showIcon={true}
      infoTooltipText={getInfoTooltipText()}
      highlightedDiscountAmount={discountTypeComponentMap[discountType]}
      highlightedDiscountAmountColor={
        discountType === 'Absolute' ? theme.color.text.primary : undefined
      }
      instructionsText={
        <ConditionalInstructionsText discount={voucherDiscount} />
      }
      bottomSection={
        showBottomSection ? (
          <BottomDiscountPart
            discountedPrice={discountedPrice}
            percentage={percentageDiscount}
            percentageFromMinimal={percentageFromMinimal}
            legalText={
              productsOnSaleIncluded ? (
                <FormattedMessage
                  {...messages.conditionalTooltipSaleIncludedText}
                />
              ) : (
                <FormattedMessage {...messages.conditionalTooltipText} />
              )
            }
          />
        ) : undefined
      }
    />
  );
};
